import { BaseService } from "../../baseService";
import { FileAPIService } from "../../fileAPIService";
import { InvoiceExportAPIService } from "./invoiceExportAPIService";
import { sanitiseParams } from "src/utils/sanitiser";
import { PostPurchaseOrdersInvoiceExportsRequestBody } from "src/types/api";

class InvoiceExportService extends BaseService<InvoiceExportAPIService> {
  private readonly fileAPIService: FileAPIService;

  constructor(apiService: InvoiceExportAPIService, fileAPIService: FileAPIService) {
    super(apiService);
    this.fileAPIService = fileAPIService;
  }

  @sanitiseParams()
  async invoiceExports(requestBody: PostPurchaseOrdersInvoiceExportsRequestBody): Promise<File[]> {
    const response = await this.apiService.postInvoiceExports(requestBody);
    const signedUrls = response.map((detail) => detail.signedUrl);
    const invoiceFiles: File[] = [];

    for (const { signedUrl, fileName } of signedUrls) {
      const fileBlob = await this.fileAPIService.getFile(signedUrl);
      const invoiceFile = new File([fileBlob], fileName, { type: fileBlob.type, lastModified: new Date().getTime() });

      invoiceFiles.push(invoiceFile);
    }

    return invoiceFiles;
  }

  @sanitiseParams()
  async getInvoiceExportDetails(requestBody: PostPurchaseOrdersInvoiceExportsRequestBody): Promise<
    {
      id: string;
      vendorName?: string;
      invoiceName?: string;
      lastModified: string;
      fileName: string;
      signedUrl: string;
      invoiceDueDate?: string;
      invoiceNote?: { body: string; date: string; tag: string };
      invoiceReferenceNumber?: string;
      totalInvoices: number;
    }[]
  > {
    const response = await this.apiService.postInvoiceExports(requestBody);

    return response.map((detail) => ({
      id: detail.id,
      vendorName: detail.vendorName,
      invoiceName: detail.invoiceName,
      lastModified: detail.lastModified,
      fileName: detail.signedUrl.fileName,
      signedUrl: detail.signedUrl.signedUrl,
      invoiceDueDate: detail.invoiceDueDate,
      invoiceNote: detail.invoiceNote,
      invoiceReferenceNumber: detail.invoiceReferenceNumber,
      totalInvoices: detail.totalInvoices
    }));
  }

  @sanitiseParams()
  async getLegacyInvoiceExportDetails(requestBody: PostPurchaseOrdersInvoiceExportsRequestBody): Promise<
    {
      id: string;
      vendorName?: string;
      invoiceName?: string;
      lastModified: string;
      fileName: string;
      signedUrl: string;
      invoiceDueDate?: string;
      invoiceNote?: { body: string; date: string; tag: string };
      invoiceReferenceNumber?: string;
      totalInvoices: number;
    }[]
  > {
    const response = await this.apiService.postLegacyInvoiceExports(requestBody);

    return response.map((detail) => ({
      id: detail.id,
      vendorName: detail.vendorName,
      invoiceName: detail.invoiceName,
      lastModified: detail.lastModified,
      fileName: detail.signedUrl.fileName,
      signedUrl: detail.signedUrl.signedUrl,
      invoiceDueDate: detail.invoiceDueDate,
      invoiceNote: detail.invoiceNote,
      invoiceReferenceNumber: detail.invoiceReferenceNumber,
      totalInvoices: detail.totalInvoices
    }));
  }
}

export { InvoiceExportService };
